/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {isTrue} from '@ion/core/src/utils/BooleanUtils';

/**
 * Provides a utility for determining if the application is current being executed
 * in development mode. The function will return true if the environment variable
 * `NEXT_PUBLIC_IS_DEV` is set to 'true', otherwise, it will return `false`.
 *
 * ```typescript
 *  import {isDevMode} from '@ion/applications/src/utils/env';
 *  ...
 *
 *  const isDev = isDevMode();
 * ```
 */
export const isDevMode = (): boolean => (
  isTrue(process?.env?.NEXT_PUBLIC_IS_DEV) || false
);

/**
 * Provides a utility for determining if analytics should be included in
 * the application. The function will return true if the environment variable
 * `NEXT_PUBLIC_INCLUDE_ANALYTICS` is set to 'true' AND the application is not
 * running in development mode, otherwise, returns `false`.
 *
 * ```typescript
 *  import {includeAnalytics} from '@ion/applications/src/utils/env';
 *  ...
 *
 *  const shouldInclude = includeAnalytics();
 * ```
 */
export const includeAnalytics = (): boolean => (
  (isTrue(process?.env?.NEXT_PUBLIC_INCLUDE_ANALYTICS) && !isDevMode()) || false
);

/**
 * Provides a utility for determining if Dynatrace RUM should be included in
 * the application. The function will return true if the environment variable
 * `NEXT_PUBLIC_INCLUDE_DYNATRACE` is set to 'true' AND the application is not
 * running in development mode, otherwise, returns `false`.
 *
 * ```typescript
 *  import {includeDynatrace} from '@ion/applications/src/utils/env';
 *  ...
 *
 *  const shouldInclude = includeDynatrace();
 * ```
 */
export const includeDynatrace = (): boolean => (
  (isTrue(process?.env?.NEXT_PUBLIC_INCLUDE_DYNATRACE) && !isDevMode()) || false
);

/**
 * Provides a utility for determining if Datadog RUM should be included in
 * the application. The function will return true if the environment variable
 * `NEXT_PUBLIC_INCLUDE_DATADOG` is set to 'true' AND the application is not
 * running in development mode, otherwise, returns `false`.
 *
 * ```typescript
 *  import {includeDatadog} from '@ion/applications/src/utils/env';
 *  ...
 *
 *  const shouldInclude = includeDatadog();
 * ```
 */
export const includeDatadog = (): boolean => (
  (isTrue(process?.env?.NEXT_PUBLIC_INCLUDE_DATADOG) && !isDevMode()) || false
);

/**
 * Provides a utility for determining if Sisense should be included in the
 * application. The function will return true if the environment variable
 * `NEXT_PUBLIC_INCLUDE_SISENSE` is set to 'true', otherwise, it will
 * return `false`.
 *
 * ```typescript
 *  import {includeSisense} from '@ion/applications/src/utils/env';
 *  ...
 *
 *  const shouldInclude = includeSisense();
 * ```
 */
export const includeSisense = (): boolean => (
  isTrue(process?.env?.NEXT_PUBLIC_INCLUDE_SISENSE) || false
);

/**
 * Provides a utility for determining if default fonts should be included
 * in the application. The function will return true if the environment
 * variable `NEXT_PUBLIC_INCLUDE_DEFAULT_FONTS` is set to 'true', otherwise,
 * returns `false`.
 *
 * ```typescript
 *  import {includeDefaultFonts} from '@ion/applications/src/utils/env';
 *  ...
 *
 *  const shouldInclude = includeDefaultFonts();
 * ```
 */
export const includeDefaultFonts = (): boolean => (
  isTrue(process?.env?.NEXT_PUBLIC_INCLUDE_DEFAULT_FONTS) || false
);
